import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import TrendIcon from "@/components/app/TrendIcon";
import PhotoCropDialog from "@/components/app/PhotoCropDialog";
import PlayerHighlights from "@/components/app/PlayerHighlights";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Camera, Share2, Trophy, KeyRound, LineChart } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/5549509/pexels-photo-5549509.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function trendOf(now, prior) {
  if (!prior || prior === 0) return "flat";
  if (now > prior + 0.5) return "up";
  if (now < prior - 0.5) return "down";
  return "flat";
}

export default function PlayerProfile() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const codeFromUrl = (searchParams.get("code") || "").trim().toUpperCase();
  const [data, setData] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [uploadCode, setUploadCode] = useState(codeFromUrl);
  const [codePromptOpen, setCodePromptOpen] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/player-stats/${slug}`).then((r) => setData(r.data));
  useEffect(() => { if (slug) load(); }, [slug]);

  // Keep local code in sync if URL param changes
  useEffect(() => { if (codeFromUrl) setUploadCode(codeFromUrl); }, [codeFromUrl]);

  const openPhotoPicker = () => {
    // If a code was provided via QR / URL param, skip the prompt and go straight to file picker
    if (codeFromUrl || uploadCode) {
      fileRef.current?.click();
    } else {
      setCodePromptOpen(true);
    }
  };

  const codeSubmitted = () => {
    if (!uploadCode.trim()) { toast.error("Enter your upload code"); return; }
    setCodePromptOpen(false);
    fileRef.current?.click();
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8_000_000) { toast.error("Image too large (max 8MB before crop)"); return; }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropConfirm = async (dataUrl) => {
    try {
      await api.post(`/players/${slug}/photo`, { photo_url: dataUrl, upload_code: uploadCode.trim().toUpperCase() });
      toast.success("Photo updated!");
      setCropSrc(null);
      // Scrub the code from the URL so it's not left in history / doesn't get shared accidentally
      if (searchParams.has("code")) {
        const next = new URLSearchParams(searchParams);
        next.delete("code");
        setSearchParams(next, { replace: true });
      }
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch { toast.error("Could not copy link"); }
  };

  if (!data) return <div className="p-10 text-ink-300">Loading player…</div>;

  const { player, season, recent_5, prior, game_log } = data;
  const goals = player.goals || {};

  const isAlumni = player.status !== "active";
  const stats = isAlumni ? season : season; // same for now; "career" wording switches

  return (
    <div className="pb-10" data-testid={`profile-${slug}`}>
      <PhotoCropDialog
        open={!!cropSrc}
        src={cropSrc}
        onClose={() => setCropSrc(null)}
        onConfirm={handleCropConfirm}
      />

      <Dialog open={codePromptOpen} onOpenChange={(v) => !v && setCodePromptOpen(false)}>
        <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="heading-3 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-burgundy" /> Photo Upload
            </DialogTitle>
            <DialogDescription className="text-ink-300">
              Enter the upload code Coach shared with you. Each player has their own unique code — this stops random visitors from replacing photos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="eyebrow">Your Upload Code</Label>
              <Input
                value={uploadCode}
                onChange={(e) => setUploadCode(e.target.value.toUpperCase())}
                placeholder="e.g. QTJGHNRR"
                className="bg-ink-800 border-ink-700 mt-2 font-mono uppercase tracking-wider text-center text-lg"
                data-testid="photo-code-input"
                autoFocus
              />
              <p className="text-[10px] text-ink-400 mt-1">Don&apos;t have a code? Ask Coach — he can find it on the Roster page.</p>
            </div>
            <Button onClick={codeSubmitted} className="w-full bg-burgundy hover:bg-burgundy-hover" data-testid="photo-code-submit">
              Continue to Upload
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/40 via-ink-950/70 to-ink-950" />
        <div className="absolute inset-0 px-6 md:px-12 flex items-end pb-6">
          <div className="flex items-end gap-5 w-full">
            <div className="relative">
              <PlayerAvatar player={player} size="xl" className="ring-4 ring-burgundy/60" />
              <button
                onClick={openPhotoPicker}
                className="absolute -bottom-2 -right-2 bg-burgundy hover:bg-burgundy-hover text-white p-2 rounded-md ring-2 ring-ink-950 transition"
                title={codeFromUrl ? "Upload your photo" : "Upload your photo (requires code from Coach)"}
                data-testid="upload-photo-btn"
              >
                <Camera className="w-4 h-4" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="eyebrow text-[11px]">NPHS Canucks · #{player.jersey_number ?? "—"} · {player.position || "—"}</p>
              <h1 className="heading-1 leading-none">{player.player_name}</h1>
              <div className="flex items-center gap-3 mt-3">
                <span className={`px-3 py-1 rounded text-xs uppercase tracking-wider font-semibold ${player.status === "active" ? "bg-green-500/20 text-green-400" : "bg-ink-700 text-ink-200"}`}>
                  {player.status === "active" ? "Active" : `Alumni${player.graduation_year ? ` '${String(player.graduation_year).slice(-2)}` : ""}`}
                </span>
                <span className="text-ink-300 text-sm">{player.grade ? `Grade ${player.grade}` : ""}</span>
              </div>
            </div>
            <Button onClick={share} className="hidden md:inline-flex bg-burgundy hover:bg-burgundy-hover" data-testid="share-profile-btn">
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 -mt-2">
        <div className="flex gap-2 flex-wrap md:mb-6 mb-2">
          <Button onClick={share} className="md:hidden bg-burgundy hover:bg-burgundy-hover" data-testid="share-profile-btn-mobile">
            <Share2 className="w-4 h-4 mr-2" /> Share Profile
          </Button>
          <Link
            to={`/player/${slug}/career`}
            className="inline-flex items-center gap-2 border border-ink-700 hover:bg-ink-800 text-ink-200 hover:text-white px-4 py-2 rounded-md text-sm font-semibold uppercase tracking-wider transition"
            data-testid="view-career-btn"
          >
            <LineChart className="w-4 h-4" /> View Full Career
          </Link>
        </div>

        {/* Summary stats with trends */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          <StatCard label="PPG" value={stats.ppg} trend={trendOf(recent_5.ppg, prior?.ppg)} />
          <StatCard label="2FG%" value={`${stats.fg2_pct}%`} trend={trendOf(recent_5.fg2_pct, prior?.fg2_pct)} />
          <StatCard label="3FG%" value={`${stats.fg3_pct}%`} trend={trendOf(recent_5.fg3_pct, prior?.fg3_pct)} />
          <StatCard label="FT%" value={`${stats.ft_pct}%`} trend={trendOf(recent_5.ft_pct, prior?.ft_pct)} />
          <StatCard label="REB" value={stats.reb_avg} trend={trendOf(recent_5.reb_avg, prior?.reb_avg)} />
          <StatCard label="AST" value={stats.ast_avg} trend={trendOf(recent_5.ast_avg, prior?.ast_avg)} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Season totals */}
          <section className="bg-ink-800 rounded-lg border border-white/5 p-6">
            <h3 className="heading-3 mb-4">{isAlumni ? "Career" : "Season"} Breakdown</h3>
            <div className="space-y-2 text-sm">
              <Row label="Games Played" value={stats.gp} />
              <Row label="Total Points" value={stats.total_points} />
              <Row label="Total Rebounds" value={stats.rebounds} />
              <Row label="Total Assists" value={stats.assists} />
              <Row label="Steals" value={stats.steals} />
              <Row label="Blocks" value={stats.blocks} />
              <Row label="Turnovers" value={stats.turnovers} />
            </div>
          </section>

          {/* Goal progress */}
          {!isAlumni && (
            <section className="bg-ink-800 rounded-lg border border-white/5 p-6">
              <h3 className="heading-3 mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-burgundy" /> Season Goals</h3>
              <GoalBar label="Points / Game" current={stats.ppg} goal={goals.ppg || 0} />
              <GoalBar label="Rebounds / Game" current={stats.reb_avg} goal={goals.reb || 0} />
              <GoalBar label="Assists / Game" current={stats.ast_avg} goal={goals.ast || 0} />
            </section>
          )}

          {/* Recent log */}
          <section className="bg-ink-800 rounded-lg border border-white/5 p-6">
            <h3 className="heading-3 mb-4">Last 5 Games</h3>
            {game_log.length === 0 ? (
              <p className="text-ink-400 text-sm">No games logged yet.</p>
            ) : (
              <div className="space-y-2">
                {game_log.slice(-5).reverse().map((s) => {
                  const pts = 2 * (s.fg2_made || 0) + 3 * (s.fg3_made || 0) + (s.ft_made || 0);
                  const fgm = (s.fg2_made || 0) + (s.fg3_made || 0);
                  const fga = (s.fg2_att || 0) + (s.fg3_att || 0);
                  const fgp = fga ? Math.round((fgm / fga) * 100) : 0;
                  return (
                    <div key={s.id} className="bg-ink-900 rounded-md p-3 flex items-center justify-between" data-testid={`game-log-${s.game.id}`}>
                      <div>
                        <p className="text-xs text-ink-400">{s.game.date}</p>
                        <p className="font-semibold text-sm">{s.game.home_away === "home" ? "vs" : "@"} {s.game.opponent}</p>
                      </div>
                      <div className="flex gap-4 text-center text-xs">
                        <Mini label="PTS" v={pts} hi />
                        <Mini label="REB" v={s.rebounds || 0} />
                        <Mini label="AST" v={s.assists || 0} />
                        <Mini label="FG%" v={`${fgp}%`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Highlight reel — public. Add button appears for coach + players scanning the QR (uploadCode present). */}
          <PlayerHighlights slug={slug} uploadCode={uploadCode} />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, trend }) {
  return (
    <div className="bg-ink-800 rounded-lg border border-white/5 p-4">
      <p className="eyebrow text-[10px]">{label}</p>
      <div className="flex items-center justify-between mt-1">
        <p className="font-display text-3xl font-black">{value}</p>
        <TrendIcon direction={trend} />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-ink-700/40 last:border-0">
      <span className="text-ink-300">{label}</span>
      <span className="font-display font-bold">{value}</span>
    </div>
  );
}

function GoalBar({ label, current, goal }) {
  const pct = goal > 0 ? Math.min(100, Math.round((current / goal) * 100)) : 0;
  return (
    <div className="mb-4">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-ink-300 uppercase tracking-wider">{label}</span>
        <span className="font-display font-bold text-white">{current} / {goal}</span>
      </div>
      <div className="h-2 bg-ink-700 rounded-full overflow-hidden">
        <div className="h-full bg-burgundy transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Mini({ label, v, hi }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-ink-400">{label}</p>
      <p className={`font-display font-bold ${hi ? "text-burgundy" : "text-white"}`}>{v}</p>
    </div>
  );
}
